// The Travel list: every booking in one dated list, filtered by kind, with what's finished
// folded away. Pure, so the rules are unit-tested; TravelScreen renders the result.

import type { Segment } from '@shared/types.ts';
import { addDays, formatDay, todayIn, toInstant } from '@shared/time.ts';

export type TravelFilter = 'all' | 'flights' | 'stays' | 'other';

export const FILTERS: Array<{ id: TravelFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'flights', label: 'Flights' },
  { id: 'stays', label: 'Stays' },
  { id: 'other', label: 'Other' },
];

export function matchesFilter(s: Segment, f: TravelFilter): boolean {
  if (f === 'flights') return s.type === 'flight';
  if (f === 'stays') return s.type === 'stay';
  if (f === 'other') return s.type !== 'flight' && s.type !== 'stay';
  return true;
}

/** Still ahead or under way: a stay you're in, a flight in the air, anything later today. */
export function isUpcoming(s: Segment, now: Date): boolean {
  const end = s.end ?? s.start;
  if (end.time) return toInstant(end).getTime() >= now.getTime();
  return end.date >= todayIn(end.timeZone, now);
}

const byStart = (a: Segment, b: Segment) => toInstant(a.start).getTime() - toInstant(b.start).getTime();

export interface DayGroup {
  date: string;
  label: string;
  segments: Segment[];
}
export interface MonthGroup {
  key: string;
  label: string;
  days: DayGroup[];
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "December", or "January 2027" when it isn't this year. */
export function monthLabel(date: string, now: Date): string {
  const [y, m] = date.split('-').map(Number);
  return y === now.getFullYear() ? MONTHS[m - 1] : `${MONTHS[m - 1]} ${y}`;
}

/** Month and day headings over bookings, oldest first (or newest first for the past). */
export function groupByDay(segments: Segment[], now: Date, newestFirst = false): MonthGroup[] {
  const sorted = [...segments].sort(byStart);
  if (newestFirst) sorted.reverse();
  const months: MonthGroup[] = [];
  for (const s of sorted) {
    const date = s.start.date;
    const key = date.slice(0, 7);
    let month = months.at(-1);
    if (month?.key !== key) {
      month = { key, label: monthLabel(date, now), days: [] };
      months.push(month);
    }
    let day = month.days.at(-1);
    if (day?.date !== date) {
      day = { date, label: formatDay(date), segments: [] };
      month.days.push(day);
    }
    day.segments.push(s);
  }
  return months;
}

export interface TravelList {
  upcoming: Segment[];
  past: Segment[];
  /** The next flight to leave, whatever the filter. */
  nextFlight?: Segment;
}

export function travelList(segments: Segment[], filter: TravelFilter, now: Date): TravelList {
  const shown = segments.filter((s) => matchesFilter(s, filter));
  const nextFlight = segments
    .filter((s) => s.type === 'flight' && s.status === 'active' && toInstant(s.start).getTime() > now.getTime())
    .sort(byStart)[0];
  return {
    upcoming: shown.filter((s) => isUpcoming(s, now)),
    past: shown.filter((s) => !isUpcoming(s, now)),
    nextFlight,
  };
}

/**
 * The bookings the upcoming list needs for layovers and the next journey: anything ending from
 * two days ago on (so a journey under way keeps its earlier legs). Opening Travel then skips
 * the years of history folded under "Show past".
 */
export function nearNow(segments: Segment[], now: Date): Segment[] {
  const from = addDays(todayIn(undefined, now), -2);
  return segments.filter((s) => (s.end ?? s.start).date >= from);
}

/** Years with past bookings, newest first: what "Show past" pages through. */
export function pastYears(past: Segment[]): Array<{ year: string; count: number }> {
  const counts = new Map<string, number>();
  for (const s of past) {
    const y = s.start.date.slice(0, 4);
    counts.set(y, (counts.get(y) ?? 0) + 1);
  }
  return [...counts].sort(([a], [b]) => b.localeCompare(a)).map(([year, count]) => ({ year, count }));
}

/** "Today", "Tomorrow", "In 61 days". */
export function untilLabel(date: string, now: Date): string {
  const days = Math.round((Date.parse(date) - Date.parse(todayIn(undefined, now))) / 86_400_000);
  if (days <= 0) return 'Today';
  return days === 1 ? 'Tomorrow' : `In ${days} days`;
}
